import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { type ChatMessage, type ChatSession, type ChatRole, type DeepPartial, type SessionInfo, type DevCenterSessionInfo, type BossReportSessionInfo, type SolutionAgentRole, ERole, EAnalyticsMode, ESolutionCode } from '@/types/chat';
import { common, getIdentityUid, setCommon } from '@/common';
import { deepMerge } from '@/utils';
import { getConversationSessions, getSolutionAgents } from '@/services';
import { mapConversationSessionToChat } from '@/utils/historyMap';
import { mindMentionDisplayText } from '@/utils/mindHost';

const HISTORY_PAGE_SIZE = 20;

function getHistoryFilterKey(): string {
  const uid = getIdentityUid();
  const storeOrOrg = common.storeId?.trim() || common.orgId?.trim() || '';
  return `${uid}::${common.tenantCode ?? ''}::${storeOrOrg}::${common.solution_code ?? ''}::${common.bizType ?? ''}`;
}

export interface ChatState {
  isOpen: boolean;
  sessions: ChatSession[];
  /** 前端本地会话 ID（仅用于 sessions 数组索引，不等同于后端 sessionId） */
  localSessionId: string | null;
  currentRole: ChatRole;
  roles: ChatRole[];
  /** 请求进行中（非流式阶段用于 loading 控制） */
  isLoading: boolean;
  /**
   * @stream-reserved
   * 流式输出进行中标志，本期非流式请求暂不使用，后续接入 SSE 时启用。
   */
  isStreaming: boolean;
  /** 当前 AI 会话凭证 */
  sessionInfo: SessionInfo | null;
  /** 推荐问题列表 */
  quickQuestions: string[];
  /** 数据分析师角色的分析模式：极速模式 / 分段模式 */
  analyticsMode: EAnalyticsMode;
  /** businessAnalysis 权限标志 */
  businessAnalysisAllowed: boolean;
  /** 当前分析任务 ID */
  taskId: string | null;
  /** 当前分析任务的 turnId，用于反馈接口 */
  turnId: string | null;
  /** 身份 uid（uid > phone > email） */
  identityUid: string;
  /** 当前历史筛选桶，身份/门店变更时重置列表 */
  historyScope: string | null;
  /** 服务端历史列表（仅 agent） */
  historySessions: ChatSession[];
  historyPage: number;
  historyHasMore: boolean;
  historyLoading: boolean;
  /** 历史详情是否禁止继续提问（当前经营分析师详情允许续聊，保持 false） */
  isHistoryReadonly: boolean;
  /** 宿主已调用 init（postMessage / Macaw.init） */
  hostInitialized: boolean;
  /** 本次会话关闭了悬浮入口（不落盘，下次 init 后重新显示） */
  floatEntryDismissed: boolean;
  /** 当前路由在 SDK 黑名单中（如 /login） */
  floatEntryRouteHidden: boolean;

  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;

  createSession: () => string;
  switchSession: (sessionId: string) => void;
  getCurrentSession: () => ChatSession | null;

  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  updateMessage: (id: string, updates: DeepPartial<ChatMessage>) => void;
  getMessage: (id: string) => ChatMessage | null;

  setCurrentRole: (role: ChatRole) => void;
  /** 根据外部业务 solution_code 从后端拉取可用角色并初始化 roles/currentRole */
  initRoles: (solutionCode: string) => Promise<void>;
  setLoading: (loading: boolean) => void;
  /**
   * @stream-reserved
   * 流式输出状态控制，本期暂不使用，后续接入 SSE 时启用。
   */
  setStreaming: (streaming: boolean) => void;
  setSessionInfo: (info: SessionInfo | null) => void;
  getDevSession: () => DevCenterSessionInfo | null;
  getBossSession: () => BossReportSessionInfo | null;
  setQuickQuestions: (questions: string[]) => void;
  setAnalyticsMode: (mode: EAnalyticsMode) => void;
  setBusinessAnalysisAllowed: (allowed: boolean) => void;
  setTaskId: (id: string | null) => void;
  setTurnId: (id: string | null) => void;
  /** 拉取历史对话；reset 时从第一页重拉 */
  loadHistory: (reset?: boolean) => Promise<void>;
  openHistoryDetail: (sessionId: string) => void;
  /** 身份变更时立即切换历史分桶 */
  syncHistoryScope: () => void;
  setHostInitialized: (initialized: boolean) => void;
  dismissFloatEntry: () => void;
  setFloatEntryRouteHidden: (hidden: boolean) => void;
}

const SCHEMA_TO_UI_ROLE: Record<string, ERole> = {
  chat: ERole.ANALYST,
  agent: ERole.BUSINESS_ANALYSIS,
};

const EMPTY_ROLE: ChatRole = {
  id: ERole.ANALYST,
  label: '',
  solutionCode: ESolutionCode.DEV_CENTER,
  agentRoleCode: 'DATA_ANALYST',
  schema: 'chat',
  isDisabled: false,
};

function mapSolutionAgentToRole(agent: SolutionAgentRole): ChatRole | null {
  const schema = agent.schema?.trim().toLowerCase();
  const id = schema ? SCHEMA_TO_UI_ROLE[schema] : undefined;
  if (!id) return null;

  const label = agent.agentRoleName?.trim() || '';

  return {
    id,
    label,
    solutionCode: agent.solutionCode,
    agentRoleCode: agent.agentRoleCode,
    schema: agent.schema,
    isDisabled: agent.isDisabled,
  };
}

const createNewSession = (): ChatSession => ({
  id: nanoid(),
  title: '新对话',
  createdAt: Date.now(),
  updatedAt: Date.now(),
  messages: [],
});

export const useChatStore = create<ChatState>()((set, get) => ({
  isOpen: false,
  sessions: [],
  localSessionId: null,
  currentRole: EMPTY_ROLE,
  roles: [],
  isLoading: false,
  isStreaming: false,
  sessionInfo: null, // 后端返回的sessionId等信息，接口中会使用到
  quickQuestions: [],
  analyticsMode: EAnalyticsMode.SEGMENT,
  businessAnalysisAllowed: false,
  taskId: null,
  turnId: null,
  identityUid: '',
  historyScope: null,
  historySessions: [],
  historyPage: 0,
  historyHasMore: true,
  historyLoading: false,
  isHistoryReadonly: false,
  hostInitialized: false,
  floatEntryDismissed: false,
  floatEntryRouteHidden: false,

  toggleDrawer: () => set((s) => ({ isOpen: !s.isOpen })),
  openDrawer: () => set({ isOpen: true }),
  closeDrawer: () => set({ isOpen: false }),

  createSession: () => {
    const { sessions, historySessions } = get();
    const historyIds = new Set(historySessions.map((s) => s.id));
    const empty = sessions.find((s) => s.messages.length === 0 && !historyIds.has(s.id));
    if (empty) {
      set({ localSessionId: empty.id, isHistoryReadonly: false });
      return empty.id;
    }
    const session = createNewSession();
    set({
      sessions: [session, ...sessions],
      localSessionId: session.id,
      isHistoryReadonly: false,
    });
    return session.id;
  },

  switchSession: (sessionId) => set({ localSessionId: sessionId }),

  getCurrentSession: () => {
    const { sessions, localSessionId } = get();
    return sessions.find((s) => s.id === localSessionId) ?? null;
  },

  addMessage: (message) => {
    const id = nanoid();
    // addMessage 是运行时入口，消息具体分支由调用方构造并在流程中约束。
    const newMsg = { ...message, id, timestamp: Date.now() } as ChatMessage;
    const { localSessionId, sessions } = get();

    if (!localSessionId) return id;

    set({
      sessions: sessions.map((s) => {
        if (s.id !== localSessionId) return s;
        const isFirstUserMsg =
          newMsg.role === 'user' &&
          !s.messages.some((m) => m.role === 'user') &&
          Boolean(newMsg.content.text?.trim());
        return {
          ...s,
          title: isFirstUserMsg
            ? mindMentionDisplayText((newMsg.content.text as string).trim())
            : s.title,
          messages: [...s.messages, newMsg],
          updatedAt: Date.now(),
        };
      }),
    });
    return id;
  },

  updateMessage: (id, updates) => {
    const { localSessionId, sessions } = get();
    if (!localSessionId) return;

    set({
      sessions: sessions.map((s) =>
        s.id === localSessionId
          ? {
            ...s,
            updatedAt: Date.now(),
            messages: s.messages.map((m) => (m.id === id ? deepMerge(m, updates) : m)),
          }
          : s,
      ),
    });
  },

  getMessage: (id: string) => {
    const { localSessionId, sessions } = get();
    if (!localSessionId) return null;
    const session = sessions.find((s) => s.id === localSessionId);
    if (!session) return null;
    return session.messages.find((m) => m.id === id) ?? null;
  },

  setCurrentRole: (role) => {
    setCommon('agentRoleCode', role.agentRoleCode);
    set({ currentRole: role });
  },
  initRoles: async (solutionCode) => {
    const prevRole = get().currentRole;
    const agents = await getSolutionAgents(solutionCode);
    const roles = agents
      .map(mapSolutionAgentToRole)
      .filter((role): role is ChatRole => Boolean(role));

    if (!roles.length) {
      set({ roles: [] });
      return;
    }

    const defaultAgent = agents.find((agent) => agent.isDefault);
    const defaultRole = defaultAgent
      ? roles.find((role) =>
        role.agentRoleCode === defaultAgent.agentRoleCode
        && role.solutionCode === defaultAgent.solutionCode,
      )
      : undefined;

    const nextRole =
      defaultRole
      ?? roles.find((role) => role.agentRoleCode === prevRole.agentRoleCode)
      ?? roles.find((role) => role.id === prevRole.id)
      ?? roles[0];

    setCommon('agentRoleCode', nextRole.agentRoleCode);
    set({
      roles,
      currentRole: nextRole,
    });
  },
  setLoading: (loading) => set({ isLoading: loading }),
  setStreaming: (streaming) => set({ isStreaming: streaming }),
  setSessionInfo: (info) => set({ sessionInfo: info }),
  getDevSession: () => {
    const { sessionInfo } = get();
    if (sessionInfo?.type === 'dev-center') {
      return sessionInfo;
    }
    return null;
  },
  getBossSession: () => {
    const { sessionInfo } = get();
    if (sessionInfo?.type === 'boss-report') {
      return sessionInfo;
    }
    return null;
  },
  setQuickQuestions: (questions) => set({ quickQuestions: questions }),
  setAnalyticsMode: (mode) => set({ analyticsMode: mode }),
  setBusinessAnalysisAllowed: (allowed) => set({ businessAnalysisAllowed: allowed }),
  setTaskId: (id) => set({ taskId: id }),
  setTurnId: (id) => set({ turnId: id }),
  syncHistoryScope: () => {
    const scope = getHistoryFilterKey();
    const identityUid = getIdentityUid();
    if (scope === get().historyScope) {
      if (identityUid !== get().identityUid) {
        set({ identityUid });
      }
      return;
    }
    set({
      historyScope: scope,
      identityUid,
      historySessions: [],
      historyPage: 0,
      historyHasMore: true,
      historyLoading: false,
      isHistoryReadonly: false,
      sessions: [],
      localSessionId: null,
      sessionInfo: null,
    });
  },
  loadHistory: async (reset = false) => {
    const { historyLoading, historyHasMore, historyPage, historySessions } = get();
    if (historyLoading) return;
    if (!reset && !historyHasMore) return;

    const page = reset ? 1 : historyPage + 1;
    set({ historyLoading: true });

    try {
      const res = await getConversationSessions(page, HISTORY_PAGE_SIZE);
      const mapped = (res.items ?? []).map(mapConversationSessionToChat);
      const next = reset ? mapped : [...historySessions, ...mapped];
      const total = res.total ?? next.length;
      set({
        historySessions: next,
        historyPage: res.page ?? page,
        historyHasMore: next.length < total,
        historyLoading: false,
      });
    } catch {
      set({
        historyLoading: false,
        historyHasMore: false,
        ...(reset ? { historySessions: [] } : {}),
      });
    }
  },
  openHistoryDetail: (sessionId) => {
    const item = get().historySessions.find((s) => s.id === sessionId);
    if (!item) return;
    const { sessions, roles, currentRole } = get();
    const exists = sessions.some((s) => s.id === sessionId);
    const baRole = roles.find((role) => role.id === ERole.BUSINESS_ANALYSIS) ?? currentRole;
    if (baRole.agentRoleCode) {
      setCommon('agentRoleCode', baRole.agentRoleCode);
    }
    set({
      sessions: exists
        ? sessions.map((s) => (s.id === sessionId ? item : s))
        : [...sessions, item],
      localSessionId: sessionId,
      currentRole: baRole,
      isHistoryReadonly: false,
      sessionInfo: { type: 'boss-report', sessionId },
    });
  },
  setHostInitialized: (initialized) =>
    set({
      hostInitialized: initialized,
      ...(initialized ? { floatEntryDismissed: false } : {}),
    }),
  dismissFloatEntry: () => set({ floatEntryDismissed: true, isOpen: false }),
  setFloatEntryRouteHidden: (hidden) =>
    set({
      floatEntryRouteHidden: hidden,
      ...(hidden ? { isOpen: false } : {}),
    }),
}));
